// IntelligenceChart.tsx
// Growth rate trend chart — bar (price) + line overlay (rent) with gradient fill.
// Uses Chart.js 4 directly (not react-chartjs-2).

import { useEffect, useRef } from 'react'
import {
  Chart,
  registerables,
  type ChartConfiguration,
  type Plugin,
} from 'chart.js'

Chart.register(...registerables)

interface TrendPoint {
  year: string
  price_chg: number
  rent_chg: number | null
}

interface IntelligenceChartProps {
  trends: TrendPoint[]
  highlight_year?: string
}

// Aggregate multiple rows per year (HTAG returns up to 3 rows/yr) into one
// averaged data point. Excludes the current partial year from the chart.
function aggregateTrends(raw: TrendPoint[]): TrendPoint[] {
  const currentYear = new Date().getFullYear()
  const byYear: Record<string, { prices: number[]; rents: number[] }> = {}

  for (const row of raw) {
    // Handle both "2024" and "2024-01-01" formats
    const y = row.year.length === 4 ? row.year : String(new Date(row.year).getFullYear())
    if (Number(y) >= currentYear) continue // exclude current partial year
    if (!byYear[y]) byYear[y] = { prices: [], rents: [] }
    // Filter out zero/null values before averaging
    if (row.price_chg !== 0) byYear[y].prices.push(row.price_chg)
    if (row.rent_chg !== null && row.rent_chg !== 0) byYear[y].rents.push(row.rent_chg)
  }

  const avg = (arr: number[]) => arr.reduce((a, b) => a + b, 0) / arr.length

  return Object.keys(byYear)
    .sort()
    .map(y => ({
      year: y,
      price_chg: byYear[y].prices.length > 0 ? avg(byYear[y].prices) : 0,
      rent_chg: byYear[y].rents.length > 0 ? avg(byYear[y].rents) : null,
    }))
}

export default function IntelligenceChart({ trends, highlight_year }: IntelligenceChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
   
  const chartRef = useRef<Chart | null>(null)

  useEffect(() => {
    const aggregated = aggregateTrends(trends)
    if (!canvasRef.current || aggregated.length === 0) return

    const ctx = canvasRef.current.getContext('2d')
    if (!ctx) return

    // Destroy previous instance to prevent canvas reuse errors
    if (chartRef.current) {
      chartRef.current.destroy()
      chartRef.current = null
    }

    const labels = aggregated.map(t => t.year)
    const prices = aggregated.map(t => t.price_chg)
    const rents = aggregated.map(t => t.rent_chg)

    // Gradient fill under rent line
    const chartHeight = canvasRef.current.offsetHeight || 300
    const gradient = ctx.createLinearGradient(0, 0, 0, chartHeight)
    gradient.addColorStop(0, 'rgba(22, 163, 74, 0.15)')
    gradient.addColorStop(1, 'rgba(22, 163, 74, 0)')

    // Zero reference line plugin
    const zeroLinePlugin: Plugin = {
      id: 'zeroLine',
      afterDraw(chart) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { ctx: c, scales } = chart as any
        if (!scales?.y) return
        const yZero = scales.y.getPixelForValue(0)
        c.save()
        c.beginPath()
        c.moveTo(scales.x.left, yZero)
        c.lineTo(scales.x.right, yZero)
        c.strokeStyle = '#E8E3D8'
        c.lineWidth = 1.5
        c.stroke()
        c.restore()
      },
    }

    // COVID boom annotation plugin
    const highlightPlugin: Plugin = {
      id: 'highlightBar',
      afterDraw(chart) {
        if (!highlight_year) return
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { ctx: c, scales, data } = chart as any
        const idx = (data.labels as string[])?.indexOf(highlight_year)
        if (idx === undefined || idx < 0) return
        if (!scales?.x || !scales?.y) return
        const x = scales.x.getPixelForValue(idx)
        c.save()
        c.setLineDash([4, 4])
        c.beginPath()
        c.moveTo(x, scales.y.top)
        c.lineTo(x, scales.y.bottom)
        c.strokeStyle = '#C8761A'
        c.lineWidth = 1
        c.stroke()
        c.fillStyle = '#C8761A'
        c.font = '10px system-ui, sans-serif'
        c.textAlign = 'center'
        c.fillText('COVID boom', x, scales.y.top - 4)
        c.restore()
      },
    }

    const config: ChartConfiguration = {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            type: 'bar',
            label: 'Price growth',
            data: prices,
            backgroundColor: prices.map(v => v >= 0 ? '#C8761A' : '#DC2626'),
            borderColor: prices.map(v => v >= 0 ? '#C8761A' : '#DC2626'),
            borderRadius: { topLeft: 3, topRight: 3 } as never,
            borderSkipped: false,
            barPercentage: 0.65,
          },
          {
            type: 'line',
            label: 'Rent growth',
            data: rents,
            borderColor: '#16A34A',
            borderWidth: 2,
            pointRadius: 3,
            pointBackgroundColor: '#16A34A',
            tension: 0.35,
            spanGaps: true,
            fill: true,
            backgroundColor: gradient,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 20 } },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#1C1A17',
            titleColor: '#F5F2EC',
            bodyColor: '#C8A882',
            borderColor: '#C8761A',
            borderWidth: 1,
            padding: 10,
            cornerRadius: 6,
            callbacks: {
              label(ctx) {
                const val = ctx.parsed.y
                if (val === null || val === undefined) return ''
                return `${ctx.dataset.label}: ${(val * 100).toFixed(1)}%`
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: { color: '#9CA3AF', font: { size: 11 } },
          },
          y: {
            border: { display: false },
            grid: { color: '#F5F2EC' },
            ticks: {
              color: '#9CA3AF',
              font: { size: 11 },
              callback(v) {
                return `${(Number(v) * 100).toFixed(0)}%`
              },
            },
          },
        },
      },
      plugins: [zeroLinePlugin, highlightPlugin],
    }

    chartRef.current = new Chart(canvasRef.current, config)

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy()
        chartRef.current = null
      }
    }
  }, [trends, highlight_year])

  const aggregated = aggregateTrends(trends)
  if (aggregated.length === 0) {
    return (
      <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: 13 }}>
        No trend data available
      </div>
    )
  }

  return (
    <div>
      {/* Custom HTML legend */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#6B6560' }}>
          <span style={{ width: 12, height: 12, borderRadius: 2, background: '#C8761A', display: 'inline-block' }} />
          Price growth
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#6B6560' }}>
          <span style={{ width: 20, height: 2, background: '#16A34A', display: 'inline-block' }} />
          Rent growth
        </span>
      </div>

      <div style={{ position: 'relative', height: 300, width: '100%' }}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  )
}
